import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ColoredMenuDownIcon } from 'assests/GmsIcons';
import Button from 'Components/DesignSystem/Button/Button';
import Search from 'Components/DesignSystem/Search/Search';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';

interface Option {
    value: string;
    label: string;
}

interface MultiSelectWithCreateProps {
    value: string[];
    onChange: (values: string[]) => void;
    options: Option[];
    onCreateNew: () => void;
    placeholder?: string;
    disabled?: boolean;
    loadOptions?: (search: string, page: number) => Promise<{ options: Option[]; hasMore: boolean }>;
    resetKey?: string;
}

const MultiSelectWithCreate = ({
    value,
    onChange,
    options,
    onCreateNew,
    placeholder = "Select values",
    disabled = false,
    loadOptions,
    resetKey,
}: MultiSelectWithCreateProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);

    // Popup position (viewport-relative). Same rationale as the Single-select
    // variant: the attribute-value dropdown in the Create Product modal lives
    // inside an ancestor with overflow-hidden/auto (the modal's scrollable
    // body, or the outer table-container on the Mapping tabs), which clips
    // any absolutely-positioned popup that extends past the cell's bottom.
    // `position: fixed` plus live viewport coords escapes the clip.
    const [popupPos, setPopupPos] = useState<{ top: number; left: number; width: number } | null>(null);

    // API pagination state
    const [apiOptions, setApiOptions] = useState<Option[]>([]);
    const [apiPage, setApiPage] = useState(1);
    const [apiHasMore, setApiHasMore] = useState(false);
    const [isApiLoading, setIsApiLoading] = useState(false);
    const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const sentinelRef = useRef<HTMLDivElement>(null);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const suppressNextFetchRef = useRef(false);

    // Handle outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                if (isOpen) {
                    closeDropdown();
                }
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isOpen]);

    // Re-measure trigger position when opened, and follow scroll/resize so
    // the popup stays glued while it's open.
    useEffect(() => {
        if (!isOpen) {
            setPopupPos(null);
            return;
        }
        const updatePos = () => {
            const rect = triggerRef.current?.getBoundingClientRect();
            if (!rect) return;
            // Clamp left so the popup never extends past the viewport's right
            // edge — otherwise a fixed popup opened near the right side pushes
            // the page to develop a horizontal scrollbar, which we don't want.
            const maxLeft = Math.max(0, window.innerWidth - rect.width - 4);
            const clampedLeft = Math.max(4, Math.min(rect.left, maxLeft));
            // Default to opening downward (top = rect.bottom). When the trigger
            // sits near the viewport bottom there isn't enough room below for
            // the 240px (tw-max-h-60) popup — without this flip the panel
            // renders off-screen with only a sliver visible. Open upward
            // instead when above has more room.
            const POPUP_MAX = 240; // matches tw-max-h-60
            const GUTTER = 4;
            const spaceBelow = window.innerHeight - rect.bottom - GUTTER;
            const spaceAbove = rect.top - GUTTER;
            const top = (spaceBelow >= POPUP_MAX || spaceBelow >= spaceAbove)
                ? rect.bottom
                : rect.top - Math.min(POPUP_MAX, Math.max(0, spaceAbove));
            setPopupPos({ top, left: clampedLeft, width: rect.width });
        };
        updatePos();
        window.addEventListener('scroll', updatePos, true);
        window.addEventListener('resize', updatePos);
        return () => {
            window.removeEventListener('scroll', updatePos, true);
            window.removeEventListener('resize', updatePos);
        };
    }, [isOpen]);

    useEffect(() => {
        setApiOptions([]);
        setApiPage(1);
        setApiHasMore(false);
        setDebouncedSearch('');
        setHasFetchedOnce(false);
    }, [resetKey]);

    // API fetch: triggered by page or debounced search changes while open
    useEffect(() => {
        if (!loadOptions || !isOpen) return;
        if (suppressNextFetchRef.current) {
            suppressNextFetchRef.current = false;
            return;
        }
        setIsApiLoading(true);
        loadOptions(debouncedSearch, apiPage)
            .then(({ options: newOpts, hasMore }) => {
                setApiHasMore(hasMore);
                setHasFetchedOnce(true);
                setApiOptions(prev =>
                    apiPage === 1
                        ? newOpts
                        : [...prev, ...newOpts.filter(o => !prev.some(p => p.value === o.value))]
                );
            })
            .finally(() => setIsApiLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiPage, debouncedSearch, isOpen]);

    // IntersectionObserver for infinite scroll sentinel
    useEffect(() => {
        if (!sentinelRef.current || !apiHasMore || isApiLoading || !loadOptions) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setApiPage(prev => prev + 1);
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [apiHasMore, isApiLoading, loadOptions]);

    const mergedOptions = useMemo(() => {
        if (!loadOptions) {
            return searchTerm
                ? options.filter(o => o.label.toLowerCase().includes(searchTerm.toLowerCase()))
                : options;
        }

        const allKnown = [...options, ...apiOptions];
        const selectedOptions: Option[] = value.map(v =>
            allKnown.find(o => o.value === v) || { value: v, label: v }
        );
        const selectedSet = new Set(value);

        if (searchTerm) {
            if (searchTerm !== debouncedSearch) {
                return selectedOptions;
            }
            const apiWithoutSelected = apiOptions.filter(o => !selectedSet.has(o.value));
            return [...selectedOptions, ...apiWithoutSelected];
        }

        const localWithoutSelected = options.filter(o => !selectedSet.has(o.value));
        const localValues = new Set(options.map(o => o.value));
        const uniqueApiOptions = apiOptions.filter(o => !localValues.has(o.value) && !selectedSet.has(o.value));
        return [...selectedOptions, ...localWithoutSelected, ...uniqueApiOptions];
    }, [options, value, searchTerm, debouncedSearch, apiOptions, loadOptions]);

    // Get display label
    const getDisplayLabel = (): string => {
        if (value.length === 0) return '';
        const allOptions = [...options, ...apiOptions];
        if (value.length === 1) {
            const option = allOptions.find(opt => opt.value === value[0]);
            return option?.label || value[0];
        }
        if (value.length === 2) {
            const labels = value
                .map(v => allOptions.find(opt => opt.value === v)?.label || v)
                .filter(Boolean);
            return labels.join(', ');
        }
        return `${value.length} Selected`;
    };

    const handleToggle = (optionValue: string) => {
        if (value.includes(optionValue)) {
            onChange(value.filter(v => v !== optionValue));
        } else {
            onChange([...value, optionValue]);
        }
    };

    const handleSelectAll = () => {
        const filteredValues = mergedOptions.map(opt => opt.value);
        const allFilteredSelected = filteredValues.every(v => value.includes(v));

        if (allFilteredSelected) {
            onChange(value.filter(v => !filteredValues.includes(v)));
        } else {
            const newValues = Array.from(new Set([...value, ...filteredValues]));
            onChange(newValues);
        }
    };

    const closeDropdown = () => {
        setIsOpen(false);
        setSearchTerm('');
        setApiOptions([]);
        setApiPage(1);
        setApiHasMore(false);
        setDebouncedSearch('');
        setHasFetchedOnce(false);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };

    const toggleDropdown = () => {
        if (!disabled) {
            if (isOpen) {
                closeDropdown();
            } else {
                setIsOpen(true);
            }
        }
    };

    const handleInputChange = (val: string) => {
        setSearchTerm(val);
        if (!loadOptions) return;
        suppressNextFetchRef.current = true;
        setApiOptions([]);
        setApiPage(1);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            suppressNextFetchRef.current = false;
            setDebouncedSearch(val);
        }, 500);
    };

    const isAllSelected = !isApiLoading &&
        searchTerm === debouncedSearch &&
        (!loadOptions || hasFetchedOnce) &&
        mergedOptions.length > 0 &&
        mergedOptions.every(opt => value.includes(opt.value));

    return (
        <div className="tw-relative tw-w-full" ref={dropdownRef}>
            <div className="tw-relative" ref={triggerRef}>
                <input data-testid="components-multiselectwithcreate-input-1"
                    type="text"
                    value={getDisplayLabel()}
                    readOnly
                    onClick={toggleDropdown}
                    placeholder={placeholder}
                    disabled={disabled}
                    title={getDisplayLabel() || undefined}
                    className={`tw-w-full tw-py-2 tw-px-3 tw-pr-8 tw-border tw-rounded tw-bg-surface tw-text-sm tw-outline-none focus:tw-ring-1 focus:tw-ring-accent-hover tw-border-edge-strong tw-cursor-pointer tw-font-medium tw-truncate ${disabled ? 'tw-bg-surface-sunken tw-cursor-not-allowed tw-text-ink-disabled placeholder:tw-text-ink-disabled' : 'tw-text-ink placeholder:tw-text-ink-secondary'
                        }`}
                />
                <button aria-label="Expand" data-testid="components-multiselectwithcreate-button-2"
                    onClick={toggleDropdown}
                    className={`tw-absolute tw-right-1 tw-top-1/2 tw--translate-y-1/2 tw-p-1 ${disabled ? 'tw-cursor-not-allowed tw-opacity-50 tw-text-ink-tertiary' : 'tw-cursor-pointer tw-text-ink'
                        }`}
                    type="button"
                    disabled={disabled}
                >
                    <ColoredMenuDownIcon />
                </button>
            </div>

            {isOpen && !disabled && popupPos && (
                <div
                    className="tw-fixed tw-bg-surface tw-border tw-border-edge tw-rounded tw-shadow-lg tw-z-[9999] tw-max-h-60 tw-overflow-y-auto no-scrollbar"
                    style={{
                        top: `${popupPos.top}px`,
                        left: `${popupPos.left}px`,
                        width: `${popupPos.width}px`,
                    }}
                >
                    <div className="tw-py-1 tw-my-1">
                        {/* Create New Button */}
                        <div className="tw--ml-1 tw-mb-2">
                            <Button
                                variant="tertiary"
                                onClick={() => {
                                    closeDropdown();
                                    onCreateNew();
                                }}
                                className="hover:tw-bg-surface"
                            >
                                Create
                            </Button>
                        </div>

                        {/* Search Box */}
                        <div className="tw-px-3 tw-mb-2">
                            <Search
                                placeholder="Search"
                                value={searchTerm}
                                onChange={handleInputChange}
                                /* § 288 — same pair `isAllSelected` below already reads
                                   (`searchTerm === debouncedSearch`), and gated on
                                   `loadOptions` for the same reason: with no fetcher the
                                   debounce is never armed. */
                                loading={!!loadOptions && (isApiLoading || searchTerm !== debouncedSearch)}
                                loadingLabel="Searching options"
                                clearable
                                searchIconPosition='end'
                            />
                        </div>

                        {/* Select All Checkbox */}
                        {mergedOptions.length > 0 && (
                            <div className="tw-px-3 tw-py-2">
                                <Checkbox
                                    label="Select All"
                                    checked={isAllSelected}
                                    onChange={handleSelectAll}
                                />
                            </div>
                        )}

                        {/* Options */}
                        {mergedOptions.length === 0 && searchTerm && !isApiLoading ? (
                            <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-ink-tertiary tw-italic">
                                No values found
                            </div>
                        ) : (
                            mergedOptions.map((option) => (
                                <div
                                    key={option.value}
                                    className="tw-px-3 tw-py-2"
                                >
                                    <Checkbox
                                        label={option.label}
                                        checked={value.includes(option.value)}
                                        onChange={() => handleToggle(option.value)}
                                    />
                                </div>
                            ))
                        )}

                        {mergedOptions.length === 0 && !searchTerm && !isApiLoading && (
                            <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-ink-tertiary tw-italic">
                                No values available
                            </div>
                        )}

                        {/* Sentinel for infinite scroll */}
                        {loadOptions && apiHasMore && !isApiLoading && (
                            <div ref={sentinelRef} className="tw-h-1" />
                        )}

                        {/* Loading indicator */}
                        {isApiLoading && (
                            <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-ink-secondary">
                                Loading...
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiSelectWithCreate;
